import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
// import { AnimatePresence } from "framer-motion"; // Commented out - background image feature disabled
import { Lock, Gamepad2, Gem } from "lucide-react";
import { cn, generateTextShadow } from "@/lib/utils";
import useUserAgent from "@/lib/hooks/useUserAgent";
import PixelBlast from "@/components/PixelBlast";
import { t } from "@/lib/i18n";

export const Route = createFileRoute("/arena/")({
  component: ArenaLanding,
});

type ArenaCard = {
  title: string;
  image: string;
  route: NavigateOptions;
  locked?: boolean;
  stats?: {
    gameModes: number;
    treasures: number;
  };
};

const ARENAS: ArenaCard[] = [
  {
    title: t("arena.footballArena"),
    image: "https://cdn.navalabs.io/map-detailed_KqW3ey.png",
    route: { to: "/home" },
    // stats: {
    //   gameModes: 5,
    //   treasures: 127,
    // },
  },
  {
    title: t("arena.simpleArena"),
    image: "/placeholder.png",
    locked: true,
    route: {
      href: "https://lite.trustmurad.info/lite/home",
      reloadDocument: true,
    },
    // stats: {
    //   gameModes: 3,
    //   treasures: 89,
    // },
  },
  {
    title: t("arena.lockedArena"),
    image: "/placeholder.png",
    locked: true,
    route: { to: "/home" },
    stats: {
      gameModes: 7,
      treasures: 256,
    },
  },
];

function ArenaLanding() {
  const navigate = useNavigate();
  const [hovered, setHovered] = React.useState<number | null>(null);
  const [selected, setSelected] = React.useState<number | null>(null);
  const { isMobile, isIOS } = useUserAgent();

  // Active card is either hovered (desktop) or selected (mobile)
  const activeCard = hovered !== null ? hovered : selected;

  // Get current background image based on active card
  // const currentBgImage = activeCard !== null ? ARENAS[activeCard].image : null; // Commented out - background image feature disabled

  const handleCardClick = (arena: ArenaCard, idx: number) => {
    if (arena.locked) {
      // If locked, just toggle selection state (mobile)
      setSelected(selected === idx ? null : idx);
    } else {
      // If unlocked, navigate to home
      navigate(arena.route);
    }
  };

  return (
    <div
      className="
      relative min-h-screen bg-white overflow-x-hidden
      flex flex-col items-center justify-center
      "
      style={{
        fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "none",
      }}
      onClick={(e) => {
        // Clear selection when clicking background (mobile)
        if (e.target === e.currentTarget) {
          setSelected(null);
        }
      }}
    >
      {/* PixelBlast background */}
      <div className="absolute inset-0 z-0 w-full h-full opacity-20">
        <PixelBlast
          variant="square"
          pixelSize={4}
          color="#A9E72F"
          patternScale={2}
          patternDensity={1}
          pixelSizeJitter={4}
          enableRipples
          rippleSpeed={1}
          rippleThickness={0.12}
          rippleIntensityScale={1.5}
          liquid={false}
          liquidStrength={0.12}
          liquidRadius={1.2}
          liquidWobbleSpeed={0}
          speed={0.5}
          edgeFade={0.075}
          transparent
          centerFadeRadius={0}
        />
      </div>

      {/* Background image layer with grayscale filter */}
      {/* <div className="absolute inset-0 z-0 hidden sm:block">
        <AnimatePresence mode="wait">
          {currentBgImage && (
            <motion.img
              key={currentBgImage}
              src={currentBgImage}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1, ease: "linear" }}
              className="
                absolute inset-0
                w-full h-full
                object-cover grayscale-20
              "
            />
          )}
        </AnimatePresence>
        <div
          className="
            absolute inset-0
            bg-linear-to-b from-green-100/60 via-green-100/40 to-green-100/80
          "
        />
        <div
          className="
            absolute inset-0
            bg-[radial-gradient(1200px_800px_at_50%_-20%,rgba(255,255,255,0.08),transparent_60%)]
          "
        />
      </div> */}

      <main
        className="
          relative max-w-7xl mx-auto
          px-4 sm:px-6 lg:px-8
          py-6 sm:py-8 lg:py-10
          lg:h-fit lg:max-h-screen
          flex flex-col w-full
        "
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "linear" }}
          className="shrink-0"
        >
          <h1
            className="
              text-2xl sm:text-3xl lg:text-5xl
              font-bold
              mb-2 sm:mb-3
              text-green-800
              px-3 sm:px-4
            "
            style={{
              transform: `perspective(100vw) rotateX(23deg)`,
              fontFamily:
                "'DeadRevolverDisplayOutlined', Helvetica, Arial, sans-serif",
              textShadow: !isMobile
                ? "var(--color-lime-200) 1px 0 0.625rem"
                : isIOS
                  ? "var(--color-lime-200) 1px 0 0.03125rem"
                  : "var(--color-lime-200) 1px 0 0.125rem",
            }}
          >
            {t("arena.pickYourArena")}
            <span
              className="inline-block"
              style={{
                transform: `perspective(100vw) rotateX(23deg) ${isMobile ? "scale(.9, 1)" : ""}`,
                animation: "blink 0.6s steps(2, end) infinite",
              }}
            >
              _
            </span>
            <style>
              {`
                @keyframes blink {
                  0% {
                    opacity: 1;
                  }
                  50%, 100% {
                    opacity: 0;
                  }
                }
              `}
            </style>
          </h1>
          {/* <p
            className="
              text-white/70
              text-sm sm:text-base lg:text-lg
              max-w-2xl
            "
          >
            Choose your battleground and embark on epic adventures. Each world
            offers unique challenges, rewards, and experiences waiting to be
            discovered.
          </p> */}
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: { staggerChildren: 0.08, ease: "linear" },
            },
          }}
          className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide p-2"
        >
          <div
            className="
              flex flex-col sm:flex-row
              justify-between
              gap-4 sm:gap-6
              h-full sm:h-100
              pb-4 sm:pb-0 w-full sm:aspect-square
            "
          >
            {ARENAS.map((a, idx) => (
              <Card
                key={a.title}
                arena={a}
                isActive={activeCard === idx}
                onHover={(active) => setHovered(active ? idx : null)}
                onClick={() => handleCardClick(a, idx)}
              />
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Card({
  arena,
  isActive,
  onHover,
  onClick,
}: {
  arena: ArenaCard;
  isActive: boolean;
  onHover?: (active: boolean) => void;
  onClick?: () => void;
}) {
  const [isDesktop, setIsDesktop] = React.useState(false);
  const { isMobile } = useUserAgent();

  React.useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 640);
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 16 },
        show: { opacity: 1, y: 0 },
      }}
      animate={{
        opacity: 1,
        // y: isActive ? -8 : 0,
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15, ease: "linear" }}
      style={{
        zIndex: isActive ? 20 : 1,
        boxShadow: isMobile
          ? "0 0 8px 0px var(--color-lime-50)"
          : "0 0 8px 0px var(--color-lime-50)",
      }}
      onHoverStart={() => onHover?.(true)}
      onHoverEnd={() => onHover?.(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`
        group relative overflow-hidden rounded-lg sm:rounded-xl shadow-sm
        bg-white border border-lime-600/40 transition-all duration-150
        cursor-pointer w-full sm:h-full flex flex-col
      `}
    >
      {/* Image section - fixed height on mobile, slides up on desktop */}
      <motion.div
        className="relative shrink-0 overflow-hidden h-48 sm:h-full"
        animate={
          isDesktop && !arena.locked
            ? { height: isActive ? "80%" : "100%" }
            : {}
        }
        transition={{ duration: 0.15, ease: "linear" }}
      >
        <img
          src={arena.image}
          alt={arena.title}
          className={cn(
            "w-full h-full",
            "object-cover object-center",
            "select-none",
            arena.locked && "grayscale brightness-50",
          )}
          loading="lazy"
        />

        {/* Title overlay on image - always visible */}
        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
          <div className="flex items-start justify-between mb-1.5 sm:mb-2">
            {!arena.locked && (
              <h3
                className="
                text-white
                text-base sm:text-lg
                font-semibold
                drop-shadow-lg
              "
                style={{
                  fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif",
                  textShadow: generateTextShadow(1.25),
                }}
              >
                {arena.title}
              </h3>
            )}
          </div>
          {arena.stats && !arena.locked && (
            <div
              className="
                flex items-center
                gap-3 sm:gap-4
                text-green-950/70
                text-xs sm:text-sm
              "
            >
              <div className="flex items-center gap-1 sm:gap-1.5">
                <Gamepad2 className="size-3 sm:size-4" />
                <span>
                  {arena.stats.gameModes} {t("arena.modes")}
                </span>
              </div>
              <div className="flex items-center gap-1 sm:gap-1.5">
                <Gem className="size-3 sm:size-4" />
                <span>
                  {arena.stats.treasures} {t("arena.treasuries")}
                </span>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Content section - always visible on mobile, revealed on hover on desktop */}
      {!arena.locked && (
        <div
          className="
            flex-1 p-3 sm:p-4 flex flex-col justify-end
            bg-green-50
          "
          style={{
            opacity: isDesktop ? (isActive ? 1 : 0) : 1,
            transition: "opacity 0.1s linear",
          }}
        >
          <button
            className="
              pointer-events-auto w-full bg-green-600 hover:bg-green-700
              border border-transparent
              rounded-sm px-3 py-2 sm:px-4 sm:py-3 text-white text-sm sm:text-base
              font-semibold transition-all duration-100 hover:shadow-lg
              hover:shadow-green-600/20 active:scale-95
              h-fit hover:cursor-pointer
            "
            style={{
              fontFamily: "'DeadRevolver', Helvetica, Arial, sans-serif",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            {t("arena.enterArena")}
          </button>
        </div>
      )}

      {/* Locked overlay */}
      {arena.locked ? (
        <div
          className="
            absolute inset-0
            backdrop-blur-sm
            grid place-items-center
          "
        >
          <div className="flex items-center gap-2 text-white">
            <Lock className="size-4 sm:size-5" />
            <span className="font-semibold text-sm sm:text-base">
              {t("arena.comingSoon")}
            </span>
          </div>
        </div>
      ) : null}

      {/* card shine effect on hover */}
      {isActive && (
        <>
          <style>
            {`
              @keyframes card-shine {
                0% {
                  left: -200%;
                }
                100% {
                  left: 200%;
                }
              }
            `}
          </style>
          <div
            className="pointer-events-none absolute top-0 w-[200%] h-full"
            style={{
              left: "-200%",
              background:
                "linear-gradient(90deg, transparent, hsla(0, 0%, 100%, 0.12), transparent)",
              transform: "skewX(-20deg)",
              animation: "card-shine 0.8s linear",
            }}
          />
        </>
      )}
    </motion.div>
  );
}
